export const PYTHON_INTERMEDIATE_PROBLEMS = [
    {
      id: 1,
      title: "Lambda Multiply",
      slug: "python-lambda-multiply-anonymous-function",
      seoTitle: "Python Lambda Multiply | Anonymous Functions | Repractiq",
      metaDescription: "Learn how to define an inline anonymous lambda function in Python to multiply two arguments together.",
      tags: ["lambda functions", "functional programming"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write an anonymous lambda function assigned to a variable named `multiply` that takes two numbers and returns their product.",
      explanation: "Lambda functions are small, anonymous one-liner expressions defined using the `lambda` keyword instead of standard `def` blocks.",
      hint: "Use syntax structure: `multiply = lambda a, b: a * b`",
      starterCode: `multiply = # your lambda function here\n`,
      expectedOutput: "",
      testCases: [
        { input: "5, 6", call: "multiply(5, 6)", expected: "30" },
        { input: "-2, 4", call: "multiply(-2, 4)", expected: "-8" },
      ],
    },
    {
      id: 2,
      title: "Safe Division Handler",
      slug: "python-safe-division-exception-handling",
      seoTitle: "Python Safe Division | Try Except Error Handling | Repractiq",
      metaDescription: "Implement safe numerical division routines wrapping ZeroDivisionError inside try-except assertion layers.",
      tags: ["error handling", "conditionals"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `safe_divide` that divides two numbers. Use a `try-except` block to capture `ZeroDivisionError` and return `None` if division by zero occurs.",
      explanation: "The `try-except` block safely runs code that could fail. Catching `ZeroDivisionError` explicitly handles calculation risks without crashing the interpreter runtime.",
      hint: "Catch `ZeroDivisionError` explicitly and return `None` in the except clause.",
      starterCode: `def safe_divide(a, b):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "10, 2", call: "safe_divide(10, 2)", expected: "5.0" },
        { input: "5, 0",  call: "safe_divide(5, 0)",  expected: "None" },
      ],
    },
    {
      id: 3,
      title: "OOP Car Class Construction",
      slug: "python-oop-car-class-instantiation",
      seoTitle: "Python OOP Car Class | Instance Attributes and Initialization | Repractiq",
      metaDescription: "Design a foundational Python class with custom dynamic instance attributes and instance state methods.",
      tags: ["OOP basics", "classes"],
      difficulty: "Intermediate",
      type: "function",
      description: "Create a class named `Car` with an `__init__` constructor accepting `make` and `model`. Include a method called `get_description` returning the string format '{make} {model}'.",
      explanation: "Classes instantiate object behaviors. The `__init__` method bounds state parameters to `self`, allowing instance methods to reference internal data structures cleanly.",
      hint: "Define `def __init__(self, make, model):` and a matching description string format function.",
      starterCode: `class Car:\n    # your class initialization and methods here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'Toyota', 'Corolla'", call: "Car('Toyota', 'Corolla').get_description()", expected: "Toyota Corolla" },
        { input: "'Ford', 'Mustang'",     call: "Car('Ford', 'Mustang').get_description()",     expected: "Ford Mustang" },
      ],
    },
    {
      id: 4,
      title: "Map Double Elements",
      slug: "python-map-double-elements-functional",
      seoTitle: "Python Map Double Elements | Functional Mapping iterators | Repractiq",
      metaDescription: "Apply functional map structures onto native lists using lambdas to cleanly double items.",
      tags: ["map/filter", "lambda functions"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `double_list` that accepts an array list of integers and applies the built-in `map()` utility along with a lambda expression to return a new list with all numbers doubled.",
      explanation: "The `map(func, iterable)` method returns an iterator applying operations cross-boundary. Passing a lambda cleanly returns scaled sets.",
      hint: "Wrap list conversions over functional mapping lines: `list(map(lambda x: x * 2, numbers))`.",
      starterCode: `def double_list(numbers):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 3]", call: "str(double_list([1, 2, 3]))", expected: "[2, 4, 6]" },
        { input: "[0, -4]",  call: "str(double_list([0, -4]))",  expected: "[0, -8]" },
      ],
    },
    {
      id: 5,
      title: "Filter Positive Numbers",
      slug: "python-filter-positive-numbers-functional",
      seoTitle: "Python Filter Positive Numbers | Functional Filters | Repractiq",
      metaDescription: "Extract strictly positive numbers using functional filter structures built alongside lambda predicates.",
      tags: ["map/filter", "lambda functions"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `get_positives` that receives an array list and processes it using the functional `filter()` method and a lambda expression to output numbers strictly greater than zero.",
      explanation: "The functional statement `filter(predicate, iterable)` screens items based on conditional booleans, leaving only matching items.",
      hint: "Use `list(filter(lambda x: x > 0, numbers))` to slice components out.",
      starterCode: `def get_positives(numbers):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[-1, 5, 0, 3]", call: "str(get_positives([-1, 5, 0, 3]))", expected: "[5, 3]" },
        { input: "[-2, -5]",      call: "str(get_positives([-2, -5]))",      expected: "[]" },
      ],
    },
    {
      id: 6,
      title: "Nested Matrix Row Summary",
      slug: "python-nested-matrix-row-summary",
      seoTitle: "Python Matrix Row Sums | Nested Data Structures | Repractiq",
      metaDescription: "Calculate inner matrix summary lists tracking independent row values in 2D array structures.",
      tags: ["nested data structures", "lists"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `row_sums` that accepts a 2D matrix list of lists and outputs a 1D list showing the calculated mathematical summary totals of each independent row matrix segment.",
      explanation: "Iterate across the nested list structures, parsing individual row grids out directly into built-in `sum()` functions.",
      hint: "Use list comprehensions evaluating rows: `[sum(row) for row in matrix]`.",
      starterCode: `def row_sums(matrix):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[[1, 2], [3, 4]]", call: "str(row_sums([[1, 2], [3, 4]]))", expected: "[3, 7]" },
        { input: "[[0], [10, 20]]",  call: "str(row_sums([[0], [10, 20]]))",  expected: "[0, 30]" },
      ],
    },
    {
      id: 7,
      title: "Custom String Title Case",
      slug: "python-custom-string-title-case-manipulation",
      seoTitle: "Python Custom Title Case | String Slicing Manipulation | Repractiq",
      metaDescription: "Manually convert sentences to title case format without utilizing the default built-in title method.",
      tags: ["string manipulation", "loops"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `to_custom_title` that takes a phrase string and custom-formats the text to capitalize the very first character of every isolated word while forcing remaining letters to lowercase.",
      explanation: "Splitting strings allows custom element logic. Target `word[0].upper() + word[1:].lower()` to build robust title formatting manually.",
      hint: "Split text into components, process boundary character capitalization rules, then call `' '.join()`.",
      starterCode: `def to_custom_title(text):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'cOdInG iS fUn'", call: "to_custom_title('cOdInG iS fUn')", expected: "Coding Is Fun" },
        { input: "'hello'",        call: "to_custom_title('hello')",        expected: "Hello" },
      ],
    },
    {
      id: 8,
      title: "Sort Strings by Absolute Length",
      slug: "python-sort-strings-by-length-key",
      seoTitle: "Python Sort Strings by Length | Custom Key Sorting | Repractiq",
      metaDescription: "Organize words within string collections based on their absolute lengths using custom sorting configurations.",
      tags: ["sorting", "strings"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `sort_by_length` that reads a list of word string configurations and sorts them by text length in ascending order.",
      explanation: "Python sorting routines accept a custom tracking argument parameter called `key`. Configuring `key=len` changes evaluation matrices cleanly.",
      hint: "Call `sorted(strings, key=len)` inside your return statement.",
      starterCode: `def sort_by_length(strings):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "['apple', 'to', 'cat']", call: "str(sort_by_length(['apple', 'to', 'cat']))", expected: "['to', 'cat', 'apple']" },
        { input: "['a', 'bb', '']",       call: "str(sort_by_length(['a', 'bb', '']))",       expected: "['', 'a', 'bb']" },
      ],
    },
    {
      id: 9,
      title: "Recursive N-th Fibonacci Calculation",
      slug: "python-recursive-nth-fibonacci-basics",
      seoTitle: "Python Recursive Fibonacci | Recursion Basics | Repractiq",
      metaDescription: "Compute the nth sequence item in Fibonacci series using structured basic mathematical recursion formulas.",
      tags: ["recursion basics", "math"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `fibonacci` that calculates the nth Fibonacci term using recursive algorithms. Assume n starts at 0.",
      explanation: "Recursion defines steps using base structural states. For Fibonacci, handling limits `n <= 1` bounds cycles before adding recursive lines together.",
      hint: "If `n <= 1`, return `n`. Otherwise, trigger `fibonacci(n-1) + fibonacci(n-2)`.",
      starterCode: `def fibonacci(n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "5", call: "fibonacci(5)", expected: "5" },
        { input: "6", call: "fibonacci(6)", expected: "8" },
        { input: "0", call: "fibonacci(0)", expected: "0" },
      ],
    },
    {
      id: 10,
      title: "Math Module Degrees Converter",
      slug: "python-math-module-degrees-radians",
      seoTitle: "Python Radians to Degrees | Math Module Integration | Repractiq",
      metaDescription: "Convert numerical angle radian properties cleanly over to standard degrees tracking using math module helpers.",
      tags: ["modules", "math"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `radians_to_degrees` that leverages Python's built-in `math` module functions to map input radian floating metrics to active angular degrees metrics.",
      explanation: "Importing modules expands system toolsets. The library calculation function `math.degrees()` maps floating parameters to exact degrees coordinates.",
      hint: "Import `math` at the top level or within the function and call `math.degrees(rad)`.",
      starterCode: `def radians_to_degrees(rad):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "3.141592653589793", call: "round(radians_to_degrees(3.141592653589793), 1)", expected: "180.0" },
        { input: "0",                 call: "radians_to_degrees(0)",                          expected: "0.0" },
      ],
    },
    {
      id: 11,
      title: "OOP Circle Area Property",
      slug: "python-oop-circle-class-methods",
      seoTitle: "Python OOP Circle Property | Instance Object Computations | Repractiq",
      metaDescription: "Build interactive geometric circle class instances updating calculated surface areas based on radius parameters.",
      tags: ["OOP basics", "classes", "math"],
      difficulty: "Intermediate",
      type: "function",
      description: "Create a class named `Circle` initializing with a `radius` tracking value. Include an instance method called `get_area` returning the circle surface area (use `3.14159` as the value of pi).",
      explanation: "Object instance parameters can feed multi-step numeric formulations. The logic maps `3.14159 * (self.radius ** 2)` dynamically.",
      hint: "Save radius parameter down to `self.radius` inside `__init__`, then calculate circle areas via area formulations.",
      starterCode: `class Circle:\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "2", call: "round(Circle(2).get_area(), 2)", expected: "12.57" },
        { input: "0", call: "Circle(0).get_area()",           expected: "0.0" },
      ],
    },
    {
      id: 12,
      title: "Value Type Error Raiser",
      slug: "python-value-type-error-raising-validation",
      seoTitle: "Python Raise ValueError | Input Verification Checks | Repractiq",
      metaDescription: "Enforce script boundaries by explicitly generating a ValueError exception when numeric parameters fail validation.",
      tags: ["error handling", "conditionals"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `verify_age` that raises a `ValueError` with the message 'Age cannot be negative' if the input `age` is less than 0, otherwise returning the age.",
      explanation: "The `raise` keyword manually triggers specific exceptions, signaling error conditions when validation rules are broken.",
      hint: "Use `raise ValueError('Age cannot be negative')` inside a validation check.",
      starterCode: `def verify_age(age):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "25", call: "verify_age(25)", expected: "25" },
        { input: "-5", call: "try:\\n    verify_age(-5)\\nexcept ValueError as e:\\n    print(str(e))", expected: "Age cannot be negative" },
      ],
    },
    {
      id: 13,
      title: "Lambda Condition Sorter Shorthand",
      slug: "python-lambda-condition-even-odd-flag",
      seoTitle: "Python Lambda Conditionals | Inline Ternary Operators | Repractiq",
      metaDescription: "Craft compact ternary validation rules within lambda expressions to return even/odd flag strings.",
      tags: ["lambda functions", "conditionals"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a lambda function assigned to a variable named `check_parity` that evaluates an integer and returns the string 'Even' if it is divisible by 2, and 'Odd' otherwise.",
      explanation: "Lambdas support inline ternary syntax conditional evaluations: `lambda x: 'Even' if x % 2 == 0 else 'Odd'`.",
      hint: "Format your expression as: `check_parity = lambda x: 'Even' if x % 2 == 0 else 'Odd'`.",
      starterCode: `check_parity = # your code here\n`,
      expectedOutput: "",
      testCases: [
        { input: "4",  call: "check_parity(4)",  expected: "Even" },
        { input: "13", call: "check_parity(13)", expected: "Odd" },
      ],
    },
    {
      id: 14,
      title: "Sort Dictionary Elements by Value",
      slug: "python-sort-dictionary-elements-by-value-tuple",
      seoTitle: "Python Sort Dictionary Elements | Custom Tuple Sorting | Repractiq",
      metaDescription: "Sort dictionary key-value entries based on their values by leveraging custom lambda key configurations.",
      tags: ["sorting", "dictionaries", "lambda functions"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `sort_dict_by_value` that takes a dictionary and returns a list of its key-value tuples sorted by value in ascending order.",
      explanation: "Passing `d.items()` into `sorted()` alongside custom indexing extraction keys like `key=lambda item: item[1]` sorts structures by value components.",
      hint: "Sort `d.items()` using key evaluation lambda target coordinates matching index position 1.",
      starterCode: `def sort_dict_by_value(d):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'a': 3, 'b': 1, 'c': 2}", call: "str(sort_dict_by_value({'a': 3, 'b': 1, 'c': 2}))", expected: "[('b', 1), ('c', 2), ('a', 3)]" },
        { input: "{'x': 10, 'y': 5}",         call: "str(sort_dict_by_value({'x': 10, 'y': 5}))",         expected: "[('y', 5), ('x', 10)]" },
      ],
    },
    {
      id: 15,
      title: "Nested Dictionary Value Lookup",
      slug: "python-nested-dictionary-value-lookup-safety",
      seoTitle: "Python Nested Dictionary Access | Deep Key lookups | Repractiq",
      metaDescription: "Safely query embedded multi-tier dictionary elements while preventing missing key exceptions.",
      tags: ["nested data structures", "dictionaries"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `get_nested_value` that takes a complex dictionary structure, a primary key, and a secondary key, and returns the embedded value. Return 'Not Found' if either key does not exist.",
      explanation: "Access deep data stores carefully. Use `.get()` method lookups sequentially or apply membership filters (`in`) to avoid missing key crashes.",
      hint: "Check configurations using standard dictionary checks: `if primary in d and secondary in d[primary]:`.",
      starterCode: `def get_nested_value(d, primary, secondary):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'user': {'id': 101}}, 'user', 'id'", call: "get_nested_value({'user': {'id': 101}}, 'user', 'id')", expected: "101" },
        { input: "{'user': {'id': 101}}, 'user', 'age'", call: "get_nested_value({'user': {'id': 101}}, 'user', 'age')", expected: "Not Found" },
      ],
    },
    {
      id: 16,
      title: "String Vowel Stripper Replacement",
      slug: "python-string-vowel-stripper-translation",
      seoTitle: "Python Vowel Stripper | Substring Target Filters | Repractiq",
      metaDescription: "Filter vowel characters out of text blocks using custom translation loops or list filtering tools.",
      tags: ["string manipulation", "list comprehension"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `remove_vowels` that reads a line of text and returns a copy with all vowel letters (both uppercase and lowercase) removed.",
      explanation: "Reconstruct strings using list comprehensions like `[c for c in text if c.lower() not in 'aeiou']` to strip out specified character patterns.",
      hint: "Filter characters against matching vowel strings, then join elements back together using `''`.",
      starterCode: `def remove_vowels(text):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'Hello World'", call: "remove_vowels('Hello World')", expected: "Hll Wrld" },
        { input: "'Python'",      call: "remove_vowels('Python')",      expected: "Pythn" },
      ],
    },
    {
      id: 17,
      title: "Recursive Factorial Implementation",
      slug: "python-recursive-factorial-algorithm",
      seoTitle: "Python Recursive Factorial | Linear Recursion Basics | Repractiq",
      metaDescription: "Calculate standard integer factorials cleanly using mathematical base case structural recursion scripts.",
      tags: ["recursion basics", "math"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `factorial_recursive` that calculates the factorial of a non-negative integer `n` using pure recursion.",
      explanation: "A recursive factorial maps `n * factorial_recursive(n - 1)`. The execution stack stops when hitting the structural base case (`n <= 1`).",
      hint: "Set the base case: if `n <= 1` return 1. Otherwise, return `n * factorial_recursive(n - 1)`.",
      starterCode: `def factorial_recursive(n):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "4", call: "factorial_recursive(4)", expected: "24" },
        { input: "0", call: "factorial_recursive(0)", expected: "1" },
      ],
    },
    {
      id: 18,
      title: "List Element Type Extractor Filter",
      slug: "python-filter-list-element-types-isinstance",
      seoTitle: "Python Element Type Filter | Type Verification | Repractiq",
      metaDescription: "Filter and isolate specific variable types from mixed arrays using isinstance logic checks.",
      tags: ["map/filter", "built-ins"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `filter_integers` that parses a mixed list of objects and filters out everything except integers.",
      explanation: "The built-in function `isinstance(variable, type)` checks if a variable matches a given type definition, which works perfectly inside conditional filters.",
      hint: "Use `isinstance(x, int)` to check if an element is a valid integer.",
      starterCode: `def filter_integers(lst):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 'a', 2.5, 3]", call: "str(filter_integers([1, 'a', 2.5, 3]))", expected: "[1, 3]" },
        { input: "['hello', True]",  call: "str(filter_integers(['hello', True]))",  expected: "[]" },
      ],
    },
    {
      id: 19,
      title: "OOP Bank Account Simulator",
      slug: "python-oop-bank-account-encapsulation",
      seoTitle: "Python OOP Bank Account | State Encapsulation | Repractiq",
      metaDescription: "Simulate a bank account object that updates internal balance states using custom deposit and withdrawal methods.",
      tags: ["OOP basics", "classes"],
      difficulty: "Intermediate",
      type: "function",
      description: "Create a class named `BankAccount` that initializes an internal `balance` attribute to 0. Add `deposit(amount)` and `withdraw(amount)` methods to modify the balance accordingly.",
      explanation: "Classes encapsulate variable tracking states. Methods modify internal instance values (`self.balance`) to keep data synchronized.",
      hint: "Update `self.balance += amount` on deposit, and subtract the amount on withdrawal operations.",
      starterCode: `class BankAccount:\n    # your class states and methods here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "100, 40", call: "acc = BankAccount(); acc.deposit(100); acc.withdraw(40); acc.balance", expected: "60" },
        { input: "50",     call: "acc = BankAccount(); acc.deposit(50); acc.balance",                 expected: "50" },
      ],
    },
    {
      id: 20,
      title: "Index Error Exception Recovery",
      slug: "python-index-error-exception-recovery-handling",
      seoTitle: "Python Index Error Recovery | Try Except Indexing | Repractiq",
      metaDescription: "Safely handle index errors when querying list elements by implementing fallbacks in try-except blocks.",
      tags: ["error handling", "lists"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `safe_get_element` that takes a list and an index. If the index is out of bounds, catch `IndexError` and return 'Out of Bounds'.",
      explanation: "Querying index positions past list sizes causes runtime crashes. Catching `IndexError` allows code to return an error string instead.",
      hint: "Wrap list element extraction statements inside standard `try-except IndexError:` blocks.",
      starterCode: `def safe_get_element(lst, idx):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[10, 20], 1", call: "safe_get_element([10, 20], 1)", expected: "20" },
        { input: "[10, 20], 5", call: "safe_get_element([10, 20], 5)", expected: "Out of Bounds" },
      ],
    },
    {
      id: 21,
      title: "Random Choice Module Picker",
      slug: "python-random-choice-module-picker",
      seoTitle: "Python Random Element Selection | Random Module | Repractiq",
      metaDescription: "Select an element from a list using selection methods from Python's built-in random library module.",
      tags: ["modules"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `pick_element` that takes a list and uses the built-in `random` module to select an element. For test consistency, seed the module with `42` inside the function before making the choice.",
      explanation: "The native library function `random.choice(iterable)` handles element selection, while `random.seed(val)` stabilizes calculations for predictable testing.",
      hint: "Import `random`, call `random.seed(42)`, then execute `random.choice(lst)`.",
      starterCode: `def pick_element(lst):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "['apple', 'banana', 'cherry']", call: "pick_element(['apple', 'banana', 'cherry'])", expected: "cherry" },
        { input: "[10, 20, 30, 40]",              call: "pick_element([10, 20, 30, 40])",              expected: "30" },
      ],
    },
    {
      id: 22,
      title: "Anagram Evaluation Validator",
      slug: "python-anagram-evaluation-validator-sorting",
      seoTitle: "Python Anagram Validator | String Character Sorting | Repractiq",
      metaDescription: "Check if two strings are anagrams by sorting their characters and comparing the results.",
      tags: ["string manipulation", "sorting"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `is_anagram` that reads two word strings and returns True if they are anagrams (contain identical character frequencies), and False otherwise.",
      explanation: "Two strings are anagrams if sorting their characters yields identical lists: `sorted(str1.lower()) == sorted(str2.lower())`.",
      hint: "Normalize text cases, sort character elements via `sorted()`, then check if the sorted outputs match.",
      starterCode: `def is_anagram(str1, str2):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'listen', 'silent'", call: "is_anagram('listen', 'silent')", expected: "True" },
        { input: "'hello', 'world'",   call: "is_anagram('hello', 'world')",   expected: "False" },
      ],
    },
    {
      id: 23,
      title: "List Element Counter Dictionary",
      slug: "python-list-element-counter-dictionary",
      seoTitle: "Python Frequency Dictionary Counter | Data Grouping | Repractiq",
      metaDescription: "Count how often each item appears in a list by compiling frequencies into a tracking dictionary.",
      tags: ["nested data structures", "dictionaries", "counting"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `frequency_counter` that records element counts from a list inside a standard output tracking dictionary.",
      explanation: "Loop through list items and update a dictionary using `d[item] = d.get(item, 0) + 1` to track item frequencies.",
      hint: "Build loop states tracking frequencies with the help of dictionary `.get(key, 0)` fallbacks.",
      starterCode: `def frequency_counter(lst):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "['a', 'b', 'a']", call: "str(sorted(frequency_counter(['a', 'b', 'a']).items()))", expected: "[('a', 2), ('b', 1)]" },
        { input: "[5, 5, 5]",       call: "str(sorted(frequency_counter([5, 5, 5]).items()))",       expected: "[5, 3]" },
      ],
    },
    {
      id: 24,
      title: "Key Error Exception Prevention",
      slug: "python-key-error-exception-prevention-try",
      seoTitle: "Python Key Error Prevention | Dictionary Try Except | Repractiq",
      metaDescription: "Safely query dictionary keys by wrapping searches in try-except blocks to catch KeyError exceptions.",
      tags: ["error handling", "dictionaries"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `safe_key_lookup` that looks up a key in a dictionary. If the key is missing, catch `KeyError` and return 'Key Not Found'.",
      explanation: "Looking up a missing key with `d[key]` raises an error. Wrapping this lookup in a `try-except KeyError` block provides a safe fallback return.",
      hint: "Use a `try` block for `d[key]` and intercept failures with `except KeyError:`.",
      starterCode: `def safe_key_lookup(d, key):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "{'x': 1}, 'x'", call: "safe_key_lookup({'x': 1}, 'x')", expected: "1" },
        { input: "{'x': 1}, 'y'", call: "safe_key_lookup({'x': 1}, 'y')", expected: "Key Not Found" },
      ],
    },
    {
      id: 25,
      title: "Sort Nested List Coordinates",
      slug: "python-sort-nested-list-coordinates-lambda",
      seoTitle: "Python Sort Multi Dimensional Coordinates | Key Sorting | Repractiq",
      metaDescription: "Sort nested lists of 2D coordinates based on their second index element using custom lambda keys.",
      tags: ["sorting", "lists", "lambda functions"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `sort_coordinates` that takes a list of 2D element coordinate sublists and sorts them by their Y value (index position 1) in ascending order.",
      explanation: "Custom sorting keys can extract values from nested positions. Setting `key=lambda coord: coord[1]` sorts coordinate pairs by their second value.",
      hint: "Pass the coordinate list into `sorted()` along with a custom lambda extraction key targeting index 1.",
      starterCode: `def sort_coordinates(coords):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[[1, 5], [3, 2], [4, 9]]", call: "str(sort_coordinates([[1, 5], [3, 2], [4, 9]]))", expected: "[[3, 2], [1, 5], [4, 9]]" },
        { input: "[[0, 0], [-1, -2]]",       call: "str(sort_coordinates([[0, 0], [-1, -2]]))",       expected: "[[-1, -2], [0, 0]]" },
      ],
    },
    {
      id: 26,
      title: "Recursive Sum of List",
      slug: "python-recursive-sum-of-list-algorithm",
      seoTitle: "Python Recursive List Sum | Reduction Recursion Basics | Repractiq",
      metaDescription: "Calculate the total sum of elements in a numerical list using a recursive reduction algorithm.",
      tags: ["recursion basics", "lists"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `sum_recursive` that computes the total sum of elements in a list using a recursive approach.",
      explanation: "Recursion splits lists into components. Add the first element to the recursively computed sum of the remaining slice: `lst[0] + sum_recursive(lst[1:])`.",
      hint: "Set the base case: if the list is empty (`not lst`), return 0.",
      starterCode: `def sum_recursive(lst):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 3, 4]", call: "sum_recursive([1, 2, 3, 4])", expected: "10" },
        { input: "[]",           call: "sum_recursive([])",           expected: "0" },
      ],
    },
    {
      id: 27,
      title: "Filter Map Combo Implementation",
      slug: "python-filter-map-combo-functional-chaining",
      seoTitle: "Python Filter Map Chain | Functional Programming Combinators | Repractiq",
      metaDescription: "Chain functional map and filter methods together to filter out odd numbers and double the remaining evens.",
      tags: ["map/filter", "lambda functions"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `process_evens` that filters a list to keep only even numbers, then maps those numbers to their doubled values, returning the final list.",
      explanation: "Combine functional techniques by passing the output of a filtered loop directly into a mapping operation to transform the data.",
      hint: "Pass `filter(lambda x: x % 2 == 0, numbers)` inside a `map()` function block.",
      starterCode: `def process_evens(numbers):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "[1, 2, 3, 4]", call: "str(process_evens([1, 2, 3, 4]))", expected: "[4, 8]" },
        { input: "[1, 3, 5]",    call: "str(process_evens([1, 3, 5]))",    expected: "[]" },
      ],
    },
    {
      id: 28,
      title: "String Character Case Inverter",
      slug: "python-string-case-inverter-swapcase",
      seoTitle: "Python Invert Character Cases | String Manipulation | Repractiq",
      metaDescription: "Invert character cases inside text strings, converting uppercase to lowercase and vice versa.",
      tags: ["string manipulation", "built-ins"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `invert_cases` that takes a string and converts all uppercase letters to lowercase, and all lowercase letters to uppercase.",
      explanation: "The built-in string method `.swapcase()` automatically converts uppercase characters to lowercase and lowercase characters to uppercase across a string.",
      hint: "Call the native `text.swapcase()` method on the input string.",
      starterCode: `def invert_cases(text):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'PyThOn 3'", call: "invert_cases('PyThOn 3')", expected: "pYtHoN 3" },
        { input: "'Hello'",    call: "invert_cases('Hello')",    expected: "hELLO" },
      ],
    },
    {
      id: 29,
      title: "OOP Inherited Student Class",
      slug: "python-oop-class-inheritance-student",
      seoTitle: "Python OOP Class Inheritance | Super Method Constructors | Repractiq",
      metaDescription: "Learn to apply OOP inheritance patterns to create a subclass that extends a base class using super constructor calls.",
      tags: ["OOP basics", "classes"],
      difficulty: "Intermediate",
      type: "function",
      description: "Create a base class `Person` that initializes a `name` attribute. Then, create a subclass `Student` that inherits from `Person`, accepts both `name` and `student_id`, and calls `super().__init__(name)` to set up the base class attribute.",
      explanation: "Inheritance allows subclasses to reuse logic from a parent class. The `super().__init__(args)` method forwards initialization parameters to the base class constructor.",
      hint: "Define `class Student(Person):` and call `super().__init__(name)` inside its constructor.",
      starterCode: `class Person:\n    def __init__(self, name):\n        self.name = name\n\nclass Student(Person):\n    # your subclass code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'Alice', 552", call: "s = Student('Alice', 552); f'{s.name} {s.student_id}'", expected: "Alice 552" },
      ],
    },
    {
      id: 30,
      title: "JSON String Parser Module Decoder",
      slug: "python-json-string-parser-module-decoder",
      seoTitle: "Python JSON Module Decoder | JSON Loads Dictionary Parsing | Repractiq",
      metaDescription: "Parse formatted JSON text strings into usable Python dictionaries using functions from the json module.",
      tags: ["modules", "dictionaries"],
      difficulty: "Intermediate",
      type: "function",
      description: "Write a function called `parse_json` that takes a valid JSON-formatted string and uses the built-in `json` module to parse it into a standard Python dictionary.",
      explanation: "The built-in `json` module provides standard serialization functions. Calling `json.loads(string)` decodes text data directly into standard dictionaries.",
      hint: "Import `json` and parse the text string using the `json.loads()` function.",
      starterCode: `def parse_json(json_string):\n    # your code here\n    pass\n`,
      expectedOutput: "",
      testCases: [
        { input: "'{\"id\": 1, \"valid\": true}'", call: "str(sorted(parse_json('{\"id\": 1, \"valid\": true}').items()))", expected: "[('id', 1), ('valid', True)]" },
      ],
    },
    {
        id: 31,
        title: "Read File Content Safely",
        slug: "python-read-file-content-safely-with-statement",
        seoTitle: "Python Read File Safely | File Handling Concepts | Repractiq",
        metaDescription: "Learn how to open and read text from files using with statement context managers in Python.",
        tags: ["file handling concepts", "error handling"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `read_file_safe` that attempts to open a file path name and return its content. Wrap it inside a `try-except` block to capture `FileNotFoundError` and return 'File Not Found' if the target file does not exist.",
        explanation: "The standard context manager `with open(filepath, 'r') as file:` ensures that system streams close safely even when processing errors intercept routines.",
        hint: "Use `try` with a `with open(...)` block, catching `FileNotFoundError` in the except block.",
        starterCode: `def read_file_safe(filepath):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'missing.txt'", call: "read_file_safe('missing.txt')", expected: "File Not Found" },
        ],
      },
      {
        id: 32,
        title: "Write Lines to File",
        slug: "python-write-lines-to-file-context-manager",
        seoTitle: "Python Write File Lines | File Handling Concepts | Repractiq",
        metaDescription: "Write individual list text rows sequentially into files using file writing streams in Python.",
        tags: ["file handling concepts", "loops"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `write_lines_to_file` that takes a file path string and a list of text string lines, writing each line into the file separated by a newline (`\\n`) character.",
        explanation: "Opening operations set with mode mode `'w'` clear preceding data blocks. Iterating via `.write()` statements pushes text records down to active storage units.",
        hint: "Open the destination using `open(filepath, 'w')` and write lines with `file.write(line + '\\n')` loops.",
        starterCode: `def write_lines_to_file(filepath, lines):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'out.txt', ['Line1', 'Line2']", call: "str(type(write_lines_to_file('out.txt', ['Line1', 'Line2'])))", expected: "<class 'NoneType'>" },
        ],
      },
      {
        id: 33,
        title: "Lambda String Length Sorting Predictor",
        slug: "python-lambda-string-length-sorting-predictor",
        seoTitle: "Python Lambda String Sorter | Lambda Functions | Repractiq",
        metaDescription: "Sort lists of alphanumeric words directly based on their inverse character lengths using custom lambdas.",
        tags: ["lambda functions", "sorting"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_descending_len` that takes a list of strings and uses a inline `lambda` parameter as a sorting key to return a new list ordered by word length in descending order.",
        explanation: "The expression `sorted(lst, key=lambda x: len(x), reverse=True)` applies custom functional criteria across element loops to control list positions.",
        hint: "Use `key=lambda s: len(s)` inside the standard `sorted()` built-in routine, along with `reverse=True`.",
        starterCode: `def sort_descending_len(strings):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['a', 'banana', 'cat']", call: "str(sort_descending_len(['a', 'banana', 'cat']))", expected: "['banana', 'cat', 'a']" },
          { input: "['hi', 'hello']",         call: "str(sort_descending_len(['hi', 'hello']))",         expected: "['hello', 'hi']" },
        ],
      },
      {
        id: 34,
        title: "Filter Out Empty Strings",
        slug: "python-filter-empty-strings-functional",
        seoTitle: "Python Filter Empty Strings | Map/Filter Utilities | Repractiq",
        metaDescription: "Clean text collection streams by extracting non-empty values through functional filter predicate structures.",
        tags: ["map/filter", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `remove_empty_strings` that takes a list of text values and applies the built-in functional `filter()` method to remove all empty strings (`''`).",
        explanation: "Evaluating strings as conditions checks their truthiness. Passing `lambda x: x != ''` as a filter predicate removes empty elements.",
        hint: "Return `list(filter(lambda s: s != '', strings))` from the evaluation block.",
        starterCode: `def remove_empty_strings(strings):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['a', '', 'b', '']", call: "str(remove_empty_strings(['a', '', 'b', '']))", expected: "['a', 'b']" },
          { input: "['', '']",          call: "str(remove_empty_strings(['', '']))",          expected: "[]" },
        ],
      },
      {
        id: 35,
        title: "Nested Matrix Diagonal Extractor",
        slug: "python-nested-matrix-diagonal-extractor",
        seoTitle: "Python Matrix Diagonal Extractor | Nested Data Structures | Repractiq",
        metaDescription: "Extract coordinate elements matching primary diagonal positions from nested square matrix grids.",
        tags: ["nested data structures", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `get_matrix_diagonal` that parses a square 2D nested list matrix and outputs a 1D array collecting elements where the row index equals the column index (`matrix[i][i]`).",
        explanation: "For any square matrix, tracking standard diagonal intersections matches row index positions exactly with column index positions.",
        hint: "Use list comprehensions iterating over lengths: `[matrix[i][i] for i in range(len(matrix))]`.",
        starterCode: `def get_matrix_diagonal(matrix):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, 2], [3, 4]]",       call: "str(get_matrix_diagonal([[1, 2], [3, 4]]))",       expected: "[1, 4]" },
          { input: "[[5, 6, 7], [8, 9, 10], [11, 12, 13]]", call: "str(get_matrix_diagonal([[5, 6, 7], [8, 9, 10], [11, 12, 13]]))", expected: "[5, 9, 13]" },
        ],
      },
      {
        id: 36,
        title: "String Palindrome Insensitive Check",
        slug: "python-string-palindrome-case-insensitive",
        seoTitle: "Python Case Insensitive Palindrome | String Manipulation | Repractiq",
        metaDescription: "Verify alphanumeric symmetry across sentences by performing case-insensitive character comparisons.",
        tags: ["string manipulation", "logic"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `is_palindrome_advanced` that evaluates if a string is a palindrome, ignoring differences in character case and punctuation marks.",
        explanation: "Converting text to lowercase and filtering out non-alphanumeric characters with `.isalnum()` prepares it for accurate symmetry checks via slicing.",
        hint: "Filter text using `c.lower() for c in text if c.isalnum()`, then verify if it matches its reversed copy.",
        starterCode: `def is_palindrome_advanced(text):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'A man, a plan, a canal: Panama'", call: "is_palindrome_advanced('A man, a plan, a canal: Panama')", expected: "True" },
          { input: "'hello'",                         call: "is_palindrome_advanced('hello')",                         expected: "False" },
        ],
      },
      {
        id: 37,
        title: "Sort Objects By Named Attribute",
        slug: "python-sort-objects-by-named-attribute-lambda",
        seoTitle: "Python Sort Objects by Attribute | Sorting paradigms | Repractiq",
        metaDescription: "Order custom instantiated object profiles based on their internal attributes using lambda sorting configurations.",
        tags: ["sorting", "OOP basics", "lambda functions"],
        difficulty: "Intermediate",
        type: "function",
        description: "Given a class instance configuration with `name` and `age` variables, write a function called `sort_people_by_age` that takes a list of these objects and returns the list sorted by `age` in ascending order.",
        explanation: "Custom sorting keys can extract instance variables from custom object classes. Setting `key=lambda person: person.age` orders the instances safely.",
        hint: "Pass the object list into `sorted()` using a key function that returns the attribute property: `key=lambda p: p.age`.",
        starterCode: `def sort_people_by_age(people_list):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "Custom Objects", call: "class P: def __init__(self, n, a): self.name, self.age = n, a\\nstr([p.name for p in sort_people_by_age([P('Bob', 30), P('Alice', 22)])])", expected: "['Alice', 'Bob']" },
        ],
      },
      {
        id: 38,
        title: "Recursive Count Down String Generator",
        slug: "python-recursive-count-down-array",
        seoTitle: "Python Recursive Count Down | Recursion Basics | Repractiq",
        metaDescription: "Construct numerical sequential count-down tracking arrays down to base elements using linear recursion.",
        tags: ["recursion basics", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `countdown_list` that accepts an integer `n` and returns a list containing numbers from `n` down to 1 generated purely through recursive function calls.",
        explanation: "Recursive functions append the current number to the list returned by the next step, stopping when the base case (`n <= 0`) is reached.",
        hint: "Base case: if `n <= 0`, return an empty list `[]`. Otherwise, return `[n] + countdown_list(n - 1)`.",
        starterCode: `def countdown_list(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "3", call: "str(countdown_list(3))", expected: "[3, 2, 1]" },
          { input: "0", call: "str(countdown_list(0))", expected: "[]" },
        ],
      },
      {
        id: 39,
        title: "Datetime Date Formatting Module Reader",
        slug: "python-datetime-date-formatting-module",
        seoTitle: "Python Datetime Format Conversions | Modules | Repractiq",
        metaDescription: "Extract current calendar date layouts out of text using string-to-date translation utilities.",
        tags: ["modules"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `get_weekday_name` that accepts a date string formatted as 'YYYY-MM-DD' and returns the full weekday name (e.g., 'Monday') using Python's built-in `datetime` module.",
        explanation: "The `datetime.strptime(string, format)` method converts text into structural date objects, while `.strftime('%A')` extracts the full weekday name string.",
        hint: "Use `datetime.datetime.strptime(date_str, '%Y-%m-%d')` followed by a call to `.strftime('%A')`.",
        starterCode: `import datetime\n\ndef get_weekday_name(date_str):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'2026-07-14'", call: "get_weekday_name('2026-07-14')", expected: "Tuesday" },
        ],
      },
      {
        id: 40,
        title: "OOP Rectangle Perimeter Calculation",
        slug: "python-oop-rectangle-perimeter",
        seoTitle: "Python OOP Rectangle Perimeter | OOP Basics | Repractiq",
        metaDescription: "Extend shape object features by adding perimeter calculations based on tracking attributes.",
        tags: ["OOP basics", "classes"],
        difficulty: "Intermediate",
        type: "function",
        description: "Create a class named `Rectangle` that initializes with `width` and `height` attributes. Add an instance method called `get_perimeter` that calculates and returns the perimeter of the rectangle.",
        explanation: "Instance attributes hold internal dimensions. Perimeter formulas evaluate expressions like `2 * (self.width + self.height)` dynamically using those attributes.",
        hint: "Define `self.width` and `self.height` inside `__init__`, then calculate the perimeter as `2 * (width + height)`.",
        starterCode: `class Rectangle:\n    # your class structure here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "5, 4", call: "Rectangle(5, 4).get_perimeter()", expected: "18" },
          { input: "10, 10", call: "Rectangle(10, 10).get_perimeter()", expected: "40" },
        ],
      },
      {
        id: 41,
        title: "File Word Counter Tracker",
        slug: "python-file-word-counter-tracker",
        seoTitle: "Python File Word Count | File Handling Concepts | Repractiq",
        metaDescription: "Read through text files using split parsing strategies to count the total number of words contained inside.",
        tags: ["file handling concepts", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `count_file_words` that reads an existing text file path and returns the total count of words found within its text boundaries.",
        explanation: "Reading files using `.read()` returns a single string. Calling `.split()` splits this string by whitespace, making it easy to count the words using `len()`.",
        hint: "Open the file, read all its text contents, apply the `.split()` method, and return the length of the resulting list.",
        starterCode: `def count_file_words(filepath):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'sample.txt'", call: "str(type(count_file_words('sample.txt')))", expected: "<class 'int'>" },
        ],
      },
      {
        id: 42,
        title: "Custom Target Exception Handling",
        slug: "python-custom-target-exception-handling",
        seoTitle: "Python Custom Exception Classes | Error Handling | Repractiq",
        metaDescription: "Design custom business logic exception classes by inheriting from Python's base Exception class.",
        tags: ["error handling", "OOP basics"],
        difficulty: "Intermediate",
        type: "function",
        description: "Define a custom exception class named `NegativeValueError` that inherits from the base `Exception` class. Write a function called `check_positive` that raises this custom exception if the input number is less than 0.",
        explanation: "Custom exceptions let you create application-specific error types. Inheriting from `Exception` lets you use your custom error classes within standard `try-except` blocks.",
        hint: "Define `class NegativeValueError(Exception): pass`, then check input boundaries using standard if conditions.",
        starterCode: `class NegativeValueError(Exception):\n    pass\n\ndef check_positive(num):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "-10", call: "try:\\n    check_positive(-10)\\nexcept NegativeValueError:\\n    print('Caught Custom Error')", expected: "Caught Custom Error" },
          { input: "5",   call: "check_positive(5)", expected: "5" },
        ],
      },
      {
        id: 43,
        title: "Lambda Value Exponent Multiplier",
        slug: "python-lambda-value-exponent-multiplier",
        seoTitle: "Python Lambda Power Functions | Lambda Functions | Repractiq",
        metaDescription: "Create functional exponent closures using high-order tracking configurations and lambda arguments.",
        tags: ["lambda functions", "functional programming"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `make_power_function` that accepts a numerical exponent `n` and returns a lambda function that raises any given base input to that power `n`.",
        explanation: "Functions can return nested lambda scopes. A function structure like `return lambda base: base ** n` captures outer variables cleanly through closures.",
        hint: "Return an internal statement configured as `lambda x: x ** n`.",
        starterCode: `def make_power_function(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "2", call: "square = make_power_function(2); square(5)", expected: "25" },
          { input: "3", call: "cube = make_power_function(3); cube(2)",     expected: "8" },
        ],
      },
      {
        id: 44,
        title: "Map Celsius Temperature Conversions",
        slug: "python-map-celsius-temperature-conversions",
        seoTitle: "Python Map Temperature Conversions | Map/Filter | Repractiq",
        metaDescription: "Batch convert numeric collections of Celsius values to Fahrenheit using functional map tools.",
        tags: ["map/filter", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `batch_celsius_to_fahrenheit` that accepts a list of Celsius temperatures and returns a new list of Fahrenheit values calculated using the functional `map()` method.",
        explanation: "The functional formula `map(lambda c: (c * 9/5) + 32, readings)` transforms whole lists of numbers efficiently without requiring manual for-loops.",
        hint: "Wrap map conversion results back into standard list containers: `list(map(...))`.",
        starterCode: `def batch_celsius_to_fahrenheit(celsius_list):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[0, 100]",  call: "str(batch_celsius_to_fahrenheit([0, 100]))",  expected: "[32.0, 212.0]" },
          { input: "[-40]",     call: "str(batch_celsius_to_fahrenheit([-40]))",     expected: "[-40.0]" },
        ],
      },
      {
        id: 45,
        title: "Nested Structures Dictionary Value Counter",
        slug: "python-nested-structures-dictionary-value-counter",
        seoTitle: "Python Nested Dictionary Aggregator | Nested Data Structures | Repractiq",
        metaDescription: "Traverse complex multi-tier object groups to aggregate inner numerical values across data models.",
        tags: ["nested data structures", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sum_nested_inventory` that takes a dictionary tracking multiple corporate department stores, where each department has an internal sub-inventory mapping values, and calculates the total sum of all items.",
        explanation: "Loop through outer layers, extract inner dictionaries, and use nested loops or `sum(dept.values())` to calculate values across all departments.",
        hint: "Use `for dept, items in inventory.items():` loops to access the inner numerical items.",
        starterCode: `def sum_nested_inventory(inventory):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'d1': {'shoes': 5}, 'd2': {'hats': 3}}", call: "sum_nested_inventory({'d1': {'shoes': 5}, 'd2': {'hats': 3}})", expected: "8" },
          { input: "{'d1': {}}",                            call: "sum_nested_inventory({'d1': {}})",                            expected: "0" },
        ],
      },
      {
        id: 46,
        title: "String Compression Counts",
        slug: "python-string-compression-counts",
        seoTitle: "Python Run Length String Compression | String Manipulation | Repractiq",
        metaDescription: "Compress repeating character sequences by replacing them with a single character followed by its repeat count.",
        tags: ["string manipulation", "loops"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `compress_string` that takes a string of characters and performs basic run-length encoding compression (e.g., 'aabcc' converts to 'a2b1c2').",
        explanation: "Loop through the characters while tracking consecutive occurrences. When a character changes, append the character and its repeat count to your result string.",
        hint: "Track matching items with a loop variable counter, appending elements as new characters are processed.",
        starterCode: `def compress_string(text):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'aabcc'", call: "compress_string('aabcc')", expected: "a2b1c2" },
          { input: "'abc'",   call: "compress_string('abc')",   expected: "a1b1c1" },
        ],
      },
      {
        id: 47,
        title: "Sort Strings Alphabetically Case Insensitive",
        slug: "python-sort-strings-alphabetically-case-insensitive",
        seoTitle: "Python Case Insensitive Sort | Sorting | Repractiq",
        metaDescription: "Sort lists of strings alphabetically while preventing uppercase characters from taking arbitrary sorting precedence.",
        tags: ["sorting", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_case_insensitive` that accepts a list of text names and returns a new list sorted alphabetically, ignoring uppercase and lowercase differences.",
        explanation: "By default, sorting functions place uppercase letters before lowercase letters. Setting `key=str.lower` processes all characters uniformly.",
        hint: "Pass `key=str.lower` as an argument into the `sorted()` built-in routine.",
        starterCode: `def sort_case_insensitive(strings):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['b', 'A', 'c']", call: "str(sort_case_insensitive(['b', 'A', 'c']))", expected: "['A', 'b', 'c']" },
          { input: "['Z', 'a']",      call: "str(sort_case_insensitive(['Z', 'a']))",      expected: "['a', 'Z']" },
        ],
      },
      {
        id: 48,
        title: "Recursive Digit Summary Calculator",
        slug: "python-recursive-digit-summary-calculator",
        seoTitle: "Python Recursive Digit Sum | Recursion Basics | Repractiq",
        metaDescription: "Sum the individual digits of a number recursively by isolating digit components using standard modulo operations.",
        tags: ["recursion basics", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sum_digits_recursive` that takes a positive integer `n` and returns the sum of all its digits calculated recursively.",
        explanation: "Isolate individual digits using modulo operations: `n % 10` extracts the last digit, while floor division `n // 10` strips it away to prepare for the next recursive step.",
        hint: "Base case: if `n < 10`, return `n`. Otherwise, return `(n % 10) + sum_digits_recursive(n // 10)`.",
        starterCode: `def sum_digits_recursive(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "123", call: "sum_digits_recursive(123)", expected: "6" },
          { input: "45",  call: "sum_digits_recursive(45)",  expected: "9" },
        ],
      },
      {
        id: 49,
        title: "Math Module Greatest Common Divisor",
        slug: "python-math-module-greatest-common-divisor-gcd",
        seoTitle: "Python Math GCD Calculation | Modules | Repractiq",
        metaDescription: "Find the greatest common divisor of two integers using built-in methods from the math module.",
        tags: ["modules", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `find_gcd` that accepts two integers and utilizes the standard `math` module to find and return their Greatest Common Divisor (GCD).",
        explanation: "Importing modules keeps your codebase clean. The utility function `math.gcd(a, b)` efficiently handles greatest common divisor calculations.",
        hint: "Import `math` and return the result of `math.gcd(a, b)` directly.",
        starterCode: `def find_gcd(a, b):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "12, 18", call: "find_gcd(12, 18)", expected: "6" },
          { input: "7, 13",  call: "find_gcd(7, 13)",  expected: "1" },
        ],
      },
      {
        id: 50,
        title: "OOP Animal Inheritance Methods",
        slug: "python-oop-animal-inheritance-methods",
        seoTitle: "Python OOP Method Overriding | OOP Basics | Repractiq",
        metaDescription: "Learn about method overriding by redefining base class behaviors within specialized inherited subclasses.",
        tags: ["OOP basics", "classes"],
        difficulty: "Intermediate",
        type: "function",
        description: "Create a base class named `Animal` with a method called `speak` that returns 'Generic Sound'. Then, create a subclass named `Dog` that overrides the `speak` method to return 'Bark'.",
        explanation: "Method overriding lets a subclass replace a parent class's behavior. Defining a method with the exact same name inside the subclass automatically overrides the parent's version.",
        hint: "Define `Dog(Animal)` and declare a matching `speak(self)` method to override the parent's sound output.",
        starterCode: `class Animal:\n    def speak(self):\n        return "Generic Sound"\n\nclass Dog(Animal):\n    # your subclass code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "", call: "Dog().speak()", expected: "Bark" },
        ],
      },
      {
        id: 51,
        title: "File Extension Counter Mapping",
        slug: "python-file-extension-counter-mapping",
        seoTitle: "Python Extension Frequency Extraction | File Handling Concepts | Repractiq",
        metaDescription: "Parse lists of file names to isolate and count their file extensions using custom frequency dictionaries.",
        tags: ["file handling concepts", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `count_extensions` that takes a list of filenames and compiles a dictionary tracking the frequency of each unique file extension (e.g., '.txt', '.py').",
        explanation: "Extract file extensions using splitting techniques like `filename.split('.')[-1]`. Then, loop through the extensions to build and update your frequency count dictionary.",
        hint: "Check for period indicators, split out extension labels, and count occurrences using a dictionary.",
        starterCode: `def count_extensions(filenames):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['a.txt', 'b.py', 'c.txt']", call: "str(sorted(count_extensions(['a.txt', 'b.py', 'c.txt']).items()))", expected: "[('py', 1), ('txt', 2)]" },
        ],
      },
      {
        id: 52,
        title: "Type Error Dynamic Type Casting Recovery",
        slug: "python-type-error-dynamic-type-casting-recovery",
        seoTitle: "Python Type Error Fallbacks | Error Handling | Repractiq",
        metaDescription: "Safely process arithmetic additions across different types by catching TypeError exceptions and applying string fallbacks.",
        tags: ["error handling", "type conversion"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `safe_add_strings` that tries to add two variables together. If a `TypeError` is raised, catch the exception, convert both variables to strings, and return their concatenated string result.",
        explanation: "Catching `TypeError` lets you intercept invalid operations (like adding a string to an integer) and apply alternative fallback behaviors, such as type casting.",
        hint: "Use a `try` block for `a + b`, and handle structural type updates inside an `except TypeError:` block.",
        starterCode: `def safe_add_strings(a, b):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "5, 10",    call: "safe_add_strings(5, 10)",    expected: "15" },
          { input: "'id_', 5", call: "safe_add_strings('id_', 5)", expected: "id_5" },
        ],
      },
      {
        id: 53,
        title: "Lambda Sorting Multi Tier Dictionary Keys",
        slug: "python-lambda-sorting-multi-tier-dictionary-keys",
        seoTitle: "Python Sort Nested Objects | Lambda Functions | Repractiq",
        metaDescription: "Sort lists of user record profiles based on nested age properties using custom sorting lambdas.",
        tags: ["lambda functions", "sorting", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_users_by_age` that receives a list of dictionary elements containing user profiles and returns the list sorted by the inner key `age` in ascending order.",
        explanation: "Setting a key like `key=lambda user: user['age']` tells the sorting function to look past the outer list index and evaluate the dictionaries by their inner age values.",
        hint: "Use `sorted(users, key=lambda u: u['age'])` to correctly sort the profiles.",
        starterCode: `def sort_users_by_age(users):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[{'name': 'A', 'age': 25}, {'name': 'B', 'age': 20}]", call: "str([u['name'] for u in sort_users_by_age([{'name': 'A', 'age': 25}, {'name': 'B', 'age': 20}])])", expected: "['B', 'A']" },
        ],
      },
      {
        id: 54,
        title: "Filter Prime Numbers",
        slug: "python-filter-prime-numbers-functional-predicate",
        seoTitle: "Python Filter Prime Numbers | Map/Filter | Repractiq",
        metaDescription: "Isolate prime numbers from data streams using standard mathematical logic checks inside functional filter routines.",
        tags: ["map/filter", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `extract_primes` that accepts a list of integers and applies the built-in `filter()` method to extract and return only the prime numbers.",
        explanation: "A helper function can determine if a number is prime. Passing this helper function as a predicate into `filter()` cleanly isolates prime values from the input list.",
        hint: "Write a helper function `is_prime(n)` that returns True or False, then use `list(filter(is_prime, numbers))`.",
        starterCode: `def extract_primes(numbers):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[2, 3, 4, 5, 6, 7]", call: "str(extract_primes([2, 3, 4, 5, 6, 7]))", expected: "[2, 3, 5, 7]" },
          { input: "[8, 9, 10]",        call: "str(extract_primes([8, 9, 10]))",        expected: "[]" },
        ],
      },
      {
        id: 55,
        title: "Nested Matrix Element Replacer Converter",
        slug: "python-nested-matrix-element-replacer",
        seoTitle: "Python Matrix Value Modifiers | Nested Data Structures | Repractiq",
        metaDescription: "Traverse nested matrix grid arrays to replace negative values with zero using nested loop indicators.",
        tags: ["nested data structures", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `zero_negative_elements` that takes a 2D matrix list of lists and returns a modified matrix where all negative values are replaced with 0.",
        explanation: "Iterating through multi-dimensional structures requires nested row and column trackers to inspect and modify individual values in place.",
        hint: "Use nested loops: `for i in range(len(matrix)):` and `for j in range(len(matrix[i])):` to update the elements.",
        starterCode: `def zero_negative_elements(matrix):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, -2], [-3, 4]]", call: "str(zero_negative_elements([[1, -2], [-3, 4]]))", expected: "[[1, 0], [0, 4]]" },
        ],
      },
      {
        id: 56,
        title: "String Substring Frequency Locator",
        slug: "python-string-substring-frequency-locator",
        seoTitle: "Python Count Substring Occurrences | String Manipulation | Repractiq",
        metaDescription: "Count overlapping substring occurrences within text patterns without relying on standard built-in count methods.",
        tags: ["string manipulation", "loops"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `count_substring_overlap` that counts how many times a target substring appears within a main text string, including overlapping instances.",
        explanation: "The built-in `.count()` method skips overlapping matches. To find all instances, use a loop that steps through the string character by character, checking slices of the text manually.",
        hint: "Use a loop with custom indexing to inspect text slices: `if main_str[i:i+len(sub)] == sub:`.",
        starterCode: `def count_substring_overlap(main_str, sub):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'ababaf', 'aba'", call: "count_substring_overlap('ababaf', 'aba')", expected: "2" },
          { input: "'hello', 'xyz'",   call: "count_substring_overlap('hello', 'xyz')",   expected: "0" },
        ],
      },
      {
        id: 57,
        title: "Sort Dictionary Matrix Keys Alphabetically",
        slug: "python-sort-dictionary-matrix-keys-alphabetically",
        seoTitle: "Python Alphabetical Key Sorting | Sorting Parallels | Repractiq",
        metaDescription: "Organize tracking dictionaries alphabetically by their key names using standard list conversion methods.",
        tags: ["sorting", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_dict_keys` that reads a target dictionary mapping structure and returns a list containing all its keys sorted in alphabetical order.",
        explanation: "Extract keys using the `.keys()` view, pass them into the `sorted()` routine, and return the sorted list.",
        hint: "Return the result of `sorted(d.keys())` directly from the function.",
        starterCode: `def sort_dict_keys(d):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'z': 1, 'a': 2}", call: "str(sort_dict_keys({'z': 1, 'a': 2}))", expected: "['a', 'z']" },
        ],
      },
      {
        id: 58,
        title: "Recursive Power Computation Calculator",
        slug: "python-recursive-power-computation-calculator",
        seoTitle: "Python Recursive Exponents | Recursion Basics | Repractiq",
        metaDescription: "Calculate mathematical powers recursively without using Python's standard arithmetic exponent operators.",
        tags: ["recursion basics", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `power_recursive` that takes a `base` and an exponent `exp` (where exp >= 0) and computes `base^exp` using recursive multiplication.",
        explanation: "A recursive power function multiplies the base by the result of the next step: `base * power_recursive(base, exp - 1)`. The process stops when the exponent hits the base case (`exp == 0`).",
        hint: "Base case: if `exp == 0`, return 1. Otherwise, return `base * power_recursive(base, exp - 1)`.",
        starterCode: `def power_recursive(base, exp):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "2, 3", call: "power_recursive(2, 3)", expected: "8" },
          { input: "5, 0", call: "power_recursive(5, 0)", expected: "1" },
        ],
      },
      {
        id: 59,
        title: "OS Module Path Existence Verifier",
        slug: "python-os-module-path-existence-verifier",
        seoTitle: "Python Check File Existence | Modules | Repractiq",
        metaDescription: "Verify if file system paths or directory names exist using functions from the built-in os module.",
        tags: ["modules", "file handling concepts"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `check_path_exists` that uses functions from the built-in `os` module to check if a specific system file path exists, returning True or False.",
        explanation: "The built-in `os` module handles operating system tasks. Calling `os.path.exists(path)` checks the system and returns a boolean indicating if the path is valid.",
        hint: "Import `os` and call `os.path.exists(path)` inside your function block.",
        starterCode: `def check_path_exists(path):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'/invalid/path'", call: "check_path_exists('/invalid/path')", expected: "False" },
        ],
      },
      {
        id: 60,
        title: "OOP Class Counter Tracker Variable",
        slug: "python-oop-class-counter-tracker-variable",
        seoTitle: "Python Static Class Variables | OOP Basics | Repractiq",
        metaDescription: "Track the total number of instantiated class objects using static class-level variables.",
        tags: ["OOP basics", "classes"],
        difficulty: "Intermediate",
        type: "function",
        description: "Create a class named `UserTracker` that contains a class-level attribute named `user_count` initialized to 0. Increment this count by 1 each time a new instance is created.",
        explanation: "Class variables are shared across all instances of a class. Incrementing `UserTracker.user_count` inside the `__init__` constructor updates this shared counter every time an object is initialized.",
        hint: "Reference the class variable directly using `UserTracker.user_count += 1` inside the constructor method.",
        starterCode: `class UserTracker:\n    user_count = 0\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "", call: "u1 = UserTracker(); u2 = UserTracker(); UserTracker.user_count", expected: "2" },
        ],
      },
      {
        id: 61,
        title: "Read File Line by Line",
        slug: "python-read-file-line-by-line-iteration",
        seoTitle: "Python Read File Line by Line | File Handling Concepts | Repractiq",
        metaDescription: "Learn how to efficiently iterate through a file line by line using an open context file object stream loop.",
        tags: ["file handling concepts", "loops"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `get_file_lines_count` that takes a file path string, opens the file using a context manager, iterates over it line by line to count the total rows, and returns that integer count.",
        explanation: "Iterating directly over a file pointer object (`for line in file:`) reads lines dynamically into memory one at a time, which prevents running out of memory on large files.",
        hint: "Open the file with `with open(filepath, 'r') as f:` and increment a counter inside a `for line in f:` loop.",
        starterCode: `def get_file_lines_count(filepath):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'empty.txt'", call: "str(type(get_file_lines_count('empty.txt')))", expected: "<class 'int'>" },
        ],
      },
      {
        id: 62,
        title: "Catch Multiple Specific Exceptions",
        slug: "python-catch-multiple-specific-exceptions",
        seoTitle: "Python Multiple Exceptions Handling | Error Handling | Repractiq",
        metaDescription: "Learn how to isolate and handle multiple error exceptions like ValueError and ZeroDivisionError concurrently.",
        tags: ["error handling"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `safe_divide_strings` that takes two string variables `a` and `b`. Convert them to integers and divide `a / b`. Handle `ValueError` by returning 'Invalid Number', and handle `ZeroDivisionError` by returning 'Cannot Divide By Zero'.",
        explanation: "A single try block can have multiple except clauses to catch different types of errors and return specific message outputs for each scenario.",
        hint: "Use `try:` followed by an `except ValueError:` clause and a separate `except ZeroDivisionError:` clause.",
        starterCode: `def safe_divide_strings(a, b):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'10', '2'", call: "safe_divide_strings('10', '2')", expected: "5.0" },
          { input: "'10', '0'", call: "safe_divide_strings('10', '0')", expected: "Cannot Divide By Zero" },
          { input: "'abc', '2'", call: "safe_divide_strings('abc', '2')", expected: "Invalid Number" },
        ],
      },
      {
        id: 63,
        title: "OOP Circle Area Method",
        slug: "python-oop-circle-area-method",
        seoTitle: "Python OOP Circle Area | OOP Basics | Repractiq",
        metaDescription: "Design custom object classes that use tracking attributes to calculate structural geometry attributes.",
        tags: ["OOP basics", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Create a class named `Circle` that initializes with a `radius` attribute. Import the `math` module and add an instance method called `get_area` that returns the area of the circle ($\\pi r^2$).",
        
        explanation: "Instance methods use `self` to read instance attributes. You can compute the area using Python's built-in `math.pi` value.",
        hint: "Inside `get_area(self)`, return `math.pi * (self.radius ** 2)`.",
        starterCode: `import math\n\nclass Circle:\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "1", call: "round(Circle(1).get_area(), 2)", expected: "3.14" },
          { input: "3", call: "round(Circle(3).get_area(), 2)", expected: "28.27" },
        ],
      },
      {
        id: 64,
        title: "Lambda Conditional Value Multiplier",
        slug: "python-lambda-conditional-value-multiplier",
        seoTitle: "Python Inline Lambda Conditionals | Lambda Functions | Repractiq",
        metaDescription: "Write compact inline ternary condition filters using lambda expressions.",
        tags: ["lambda functions", "conditionals"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `get_status_lambda` that returns an inline anonymous `lambda` function. The lambda should accept an integer score and return 'Pass' if the score is 50 or greater, otherwise 'Fail'.",
        explanation: "Lambdas support standard ternary syntax expressions structured as `true_val if condition else false_val` to handle simple conditional evaluations.",
        hint: "Return a lambda function defined as `lambda score: 'Pass' if score >= 50 else 'Fail'`.",
        starterCode: `def get_status_lambda():\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "", call: "f = get_status_lambda(); f(65)", expected: "Pass" },
          { input: "", call: "f = get_status_lambda(); f(45)", expected: "Fail" },
        ],
      },
      {
        id: 65,
        title: "Map String Length Conversions",
        slug: "python-map-string-length-conversions",
        seoTitle: "Python Map String Lengths | Map/Filter | Repractiq",
        metaDescription: "Batch process alphanumeric array lists into character length summaries using functional map methods.",
        tags: ["map/filter", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `get_string_lengths` that takes a list of strings and uses the built-in functional `map()` method to return a new list containing the length of each string.",
        explanation: "The `map()` function applies a transformation to every element in a list. Passing the built-in `len` function directly transforms words into their character count values.",
        hint: "Return `list(map(len, string_list))` to map the built-in length calculations over the items.",
        starterCode: `def get_string_lengths(string_list):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['hi', 'apple']", call: "str(get_string_lengths(['hi', 'apple']))", expected: "[2, 5]" },
          { input: "[]",            call: "str(get_string_lengths([]))",            expected: "[]" },
        ],
      },
      {
        id: 66,
        title: "Nested Structures Key Path Finder",
        slug: "python-nested-structures-key-path-finder",
        seoTitle: "Python Nested Dictionary Key Traversal | Nested Data Structures | Repractiq",
        metaDescription: "Safely read information out of deeply nested multi-tier dictionary matrices using key list traversals.",
        tags: ["nested data structures", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `extract_nested_value` that takes a deep nested dictionary object and a list of string keys representing a path. Navigate through the keys to return the target value. Return `None` if any key path fails.",
        explanation: "Loop through the keys in the path while updating a temporary reference pointer variable. Check if the pointer is still a dictionary before attempting to read the next key.",
        hint: "Use a loop to iterate through the path, updating `current_dict = current_dict.get(key)` while ensuring the structure remains valid.",
        starterCode: `def extract_nested_value(data, path):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'a': {'b': 42}}, ['a', 'b']", call: "extract_nested_value({'a': {'b': 42}}, ['a', 'b'])", expected: "42" },
          { input: "{'a': 2}, ['x', 'y']",         call: "str(extract_nested_value({'a': 2}, ['x', 'y']))",     expected: "None" },
        ],
      },
      {
        id: 67,
        title: "String Character Frequency Counter",
        slug: "python-string-character-frequency-counter",
        seoTitle: "Python Character Frequency Dictionary | String Manipulation | Repractiq",
        metaDescription: "Count character frequencies in text strings using frequency dictionaries.",
        tags: ["string manipulation", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `char_frequency` that takes a string and returns a dictionary where each key is a character and the value is the number of times it appears in the text.",
        explanation: "Iterate through the string character by character, incrementing its count in a dictionary. Using the `.get(char, 0)` method makes it easy to set default values for new characters.",
        hint: "Initialize an empty dictionary, loop through the string, and update counts using `counts[c] = counts.get(c, 0) + 1`.",
        starterCode: `def char_frequency(text):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'aba'", call: "str(sorted(char_frequency('aba').items()))", expected: "[('a', 2), ('b', 1)]" },
        ],
      },
      {
        id: 68,
        title: "Sort Custom Tuple Records",
        slug: "python-sort-custom-tuple-records-lambda",
        seoTitle: "Python Sort Tuples by Index | Sorting | Repractiq",
        metaDescription: "Sort complex multi-element tuple records by specific index keys using custom sorting lambdas.",
        tags: ["sorting", "lambda functions"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_tuples_by_second` that accepts a list of tuples and returns a new list sorted in ascending order by the second item in each tuple.",
        explanation: "The `sorted()` function takes a custom `key` argument. Passing a lambda like `key=lambda x: x[1]` tells it to sort the list using the second element of each tuple.",
        hint: "Pass `key=lambda t: t[1]` directly into your `sorted()` function call.",
        starterCode: `def sort_tuples_by_second(tuple_list):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[(1, 9), (2, 3)]", call: "str(sort_tuples_by_second([(1, 9), (2, 3)]))", expected: "[(2, 3), (1, 9)]" },
        ],
      },
      {
        id: 69,
        title: "Recursive String Reversal",
        slug: "python-recursive-string-reversal",
        seoTitle: "Python Recursive String Reversal | Recursion Basics | Repractiq",
        metaDescription: "Learn how to reverse text strings without slicing by using recursive function calls.",
        tags: ["recursion basics", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `reverse_recursive` that takes a string and returns a reversed copy calculated purely through recursive calls.",
        explanation: "A string can be reversed recursively by taking its last character (or the first character) and appending it to the result of reversing the remaining substring.",
        hint: "Base case: if the string length is 0 or 1, return it. Otherwise, return `reverse_recursive(s[1:]) + s[0]`.",
        starterCode: `def reverse_recursive(s):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'cat'", call: "reverse_recursive('cat')", expected: "tac" },
          { input: "''",    call: "reverse_recursive('')",    expected: "" },
        ],
      },
      {
        id: 70,
        title: "Random Choice Selection Module Selector",
        slug: "python-random-choice-selection-module",
        seoTitle: "Python Random Element Selection | Modules | Repractiq",
        metaDescription: "Use Python's random module to safely select elements from a collection.",
        tags: ["modules"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `pick_random_item` that imports Python's built-in `random` module and selects a random element from a provided list.",
        explanation: "The `random.choice(iterable)` function picks a random item from a non-empty sequence, making it easy to add randomized behavior to your programs.",
        hint: "Import the `random` module inside or outside the function and return the result of `random.choice(items)`.",
        starterCode: `def pick_random_item(items):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['a', 'b']", call: "pick_random_item(['a', 'b']) in ['a', 'b']", expected: "True" },
        ],
      },
      {
        id: 71,
        title: "File Check CSV Delimiter Extractor",
        slug: "python-file-check-csv-delimiter-extractor",
        seoTitle: "Python CSV Row Parsing | File Handling Concepts | Repractiq",
        metaDescription: "Parse structured comma-separated data files line by line using basic string splitting methods.",
        tags: ["file handling concepts", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `parse_csv_first_column` that reads a text file containing comma-separated row lines and returns a list containing only the first item from each row.",
        explanation: "When parsing simple text-based data tables, you can loop through the lines, strip away whitespace with `.strip()`, and split the values by commas using `.split(',')`.",
        hint: "Open the file, iterate through the lines, split each line by commas, and append `row[0]` to your results list.",
        starterCode: `def parse_csv_first_column(filepath):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'mock.csv'", call: "str(type(parse_csv_first_column('mock.csv')))", expected: "<class 'lists'>" },
        ],
      },
      {
        id: 72,
        title: "Index Error Fallback Recovery",
        slug: "python-index-error-fallback-recovery",
        seoTitle: "Python Handle Out of Bounds Index | Error Handling | Repractiq",
        metaDescription: "Safely read from list indices by catching IndexError exceptions and returning default values.",
        tags: ["error handling", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `safe_get_index` that accepts a list, an integer index, and a default fallback value. Attempt to return the item at that index. If an `IndexError` occurs, catch it and return the default value.",
        explanation: "Using a `try-except IndexError` block protects your program from crashing when it attempts to read a list index that is out of bounds.",
        hint: "Wrap the expression `return lst[index]` in a try block, and use `except IndexError:` to return your fallback value.",
        starterCode: `def safe_get_index(lst, index, default):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[10, 20], 1, -1", call: "safe_get_index([10, 20], 1, -1)", expected: "20" },
          { input: "[10, 20], 5, -1", call: "safe_get_index([10, 20], 5, -1)", expected: "-1" },
        ],
      },
      {
        id: 73,
        title: "OOP Class Inheritance Constructor Extension",
        slug: "python-oop-class-inheritance-constructor-extension",
        seoTitle: "Python Subclass Constructor Super | OOP Basics | Repractiq",
        metaDescription: "Extend subclass properties while keeping parent constructors intact using the super() function.",
        tags: ["OOP basics", "classes"],
        difficulty: "Intermediate",
        type: "function",
        description: "Create a base class named `Person` that initializes with a `name` attribute. Then, create a subclass named `Employee` that inherits from `Person` and adds an extra `salary` attribute using the `super()` method.",
        explanation: "Calling `super().__init__(args)` inside a subclass constructor runs the parent class's initialization logic before adding the subclass's unique attributes.",
        hint: "Inside `Employee.__init__`, call `super().__init__(name)` and then set `self.salary = salary`.",
        starterCode: `class Person:\n    def __init__(self, name):\n        self.name = name\n\nclass Employee(Person):\n    # your subclass code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "", call: "emp = Employee('Alice', 50000); f'{emp.name}-{emp.salary}'", expected: "Alice-50000" },
        ],
      },
      {
        id: 74,
        title: "Lambda Custom Dictionary Sorting Key",
        slug: "python-lambda-custom-dictionary-sorting-key",
        seoTitle: "Python Sort Dictionary by Value | Lambda Functions | Repractiq",
        metaDescription: "Sort dictionary items based on their values instead of their keys using inline lambda mapping functions.",
        tags: ["lambda functions", "sorting", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_dict_by_value` that takes a dictionary and returns its items (`(key, value)` tuples) sorted in ascending order by value.",
        explanation: "You can convert a dictionary into a list of tuples using `.items()`. Passing `key=lambda item: item[1]` into `sorted()` orders these tuples by their values.",
        hint: "Use `sorted(d.items(), key=lambda x: x[1])` to sort the dictionary items.",
        starterCode: `def sort_dict_by_value(d):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'apple': 5, 'banana': 2}", call: "str(sort_dict_by_value({'apple': 5, 'banana': 2}))", expected: "[('banana', 2), ('apple', 5)]" },
        ],
      },
      {
        id: 75,
        title: "Filter Negative Values Out",
        slug: "python-filter-negative-values-out",
        seoTitle: "Python Filter Negative Numbers | Map/Filter | Repractiq",
        metaDescription: "Remove negative numbers from a list using functional filter predicates.",
        tags: ["map/filter", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `keep_positive_numbers` that accepts a list of numbers and uses the built-in functional `filter()` method to remove all negative values (keeping numbers >= 0).",
        explanation: "The `filter()` function extracts elements that match a specific condition. Passing the predicate `lambda x: x >= 0` removes negative numbers from the list.",
        hint: "Return your filtered results wrapped in a standard list container: `list(filter(lambda x: x >= 0, numbers))`.",
        starterCode: `def keep_positive_numbers(numbers):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[-1, 0, 5]", call: "str(keep_positive_numbers([-1, 0, 5]))", expected: "[0, 5]" },
          { input: "[-3, -2]",  call: "str(keep_positive_numbers([-3, -2]))",  expected: "[]" },
        ],
      },
      {
        id: 76,
        title: "Nested Lists Flatten Matrix",
        slug: "python-nested-lists-flatten-matrix",
        seoTitle: "Python Flatten 2D List | Nested Data Structures | Repractiq",
        metaDescription: "Convert a two-dimensional nested list matrix into a flat one-dimensional list using nested loops.",
        tags: ["nested data structures", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `flatten_matrix` that takes a 2D nested list matrix and flattens it into a single 1D list containing all the individual elements.",
        explanation: "You can flatten a 2D matrix by using nested loops or a double list comprehension to pull elements out of the inner rows and add them to a single flat list.",
        hint: "Use the list comprehension `[element for row in matrix for element in row]` to quickly flatten the structure.",
        starterCode: `def flatten_matrix(matrix):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, 2], [3]]", call: "str(flatten_matrix([[1, 2], [3]]))", expected: "[1, 2, 3]" },
          { input: "[[], [5]]",    call: "str(flatten_matrix([[], [5]]))",    expected: "[5]" },
        ],
      },
      {
        id: 77,
        title: "String Whitespace Normalizer Stripper",
        slug: "python-string-whitespace-normalizer-stripper",
        seoTitle: "Python Normalize Text Whitespace | String Manipulation | Repractiq",
        metaDescription: "Clean up text strings by removing extra spaces and normalizing internal whitespace.",
        tags: ["string manipulation"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `normalize_spaces` that takes a string, removes any leading or trailing spaces, and replaces multiple consecutive spaces between words with a single space.",
        explanation: "Calling `.split()` with no arguments automatically splits a string by any amount of whitespace and removes leading/trailing spaces. Rejoining the words with `' '.join()` normalizes the spacing.",
        hint: "Combine the split and join methods: `' '.join(text.split())`.",
        starterCode: `def normalize_spaces(text):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'  hello   world  '", call: "normalize_spaces('  hello   world  ')", expected: "hello world" },
          { input: "'spaced'",          call: "normalize_spaces('spaced')",          expected: "spaced" },
        ],
      },
      {
        id: 78,
        title: "Sort Strings By Vowel Count",
        slug: "python-sort-strings-by-vowel-count-lambda",
        seoTitle: "Python Sort by Vowel Count | Sorting | Repractiq",
        metaDescription: "Sort lists of text strings based on the number of vowels they contain using custom sorting lambdas.",
        tags: ["sorting", "lambda functions", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_by_vowel_count` that takes a list of strings and sorts them in descending order based on the number of vowels (`a, e, i, o, u`) they contain.",
        explanation: "You can write a helper function or an inline lambda that counts the vowels in a string, then pass it as the `key` argument into `sorted(..., reverse=True)`.",
        hint: "Use a key function like `key=lambda s: sum(1 for c in s.lower() if c in 'aeiou')`, and remember to set `reverse=True`.",
        starterCode: `def sort_by_vowel_count(strings):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['art', 'banana']", call: "str(sort_by_vowel_count(['art', 'banana']))", expected: "['banana', 'art']" },
        ],
      },
      {
        id: 79,
        title: "Recursive List Summation",
        slug: "python-recursive-list-summation",
        seoTitle: "Python Recursive List Sum | Recursion Basics | Repractiq",
        metaDescription: "Calculate the total sum of a list of numbers recursively without using standard loops or the built-in sum function.",
        tags: ["recursion basics", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sum_list_recursive` that takes a list of numbers and calculates their total sum using recursion instead of loops.",
        explanation: "A list can be summed recursively by adding its first element to the sum of the remaining elements (`list[1:]`). The process stops when it reaches an empty list base case.",
        hint: "Base case: if the list is empty, return 0. Otherwise, return `numbers[0] + sum_list_recursive(numbers[1:])`.",
        starterCode: `def sum_list_recursive(numbers):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 3]", call: "sum_list_recursive([1, 2, 3])", expected: "6" },
          { input: "[]",        call: "sum_list_recursive([])",        expected: "0" },
        ],
      },
      {
        id: 80,
        title: "JSON Module Parser Converter",
        slug: "python-json-module-parser-converter",
        seoTitle: "Python Parse JSON String | Modules | Repractiq",
        metaDescription: "Convert structured JSON text strings into standard Python dictionary objects using the built-in json module.",
        tags: ["modules", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `parse_json_metadata` that takes a JSON-formatted text string and uses Python's built-in `json` module to parse it into a standard dictionary.",
        explanation: "The `json.loads(string)` function parses valid JSON text and converts it into a native Python dictionary or list structure.",
        hint: "Import `json` and pass the target string directly into `json.loads()`.",
        starterCode: `import json\n\ndef parse_json_metadata(json_string):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'{\"id\": 1}'", call: "parse_json_metadata('{\"id\": 1}')['id']", expected: "1" },
        ],
      },
      {
        id: 81,
        title: "Append Text Content safely to File",
        slug: "python-append-text-content-safely-to-file",
        seoTitle: "Python Append to File | File Handling Concepts | Repractiq",
        metaDescription: "Learn how to safely add new text content to the end of an existing file without overwriting it.",
        tags: ["file handling concepts"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `append_log_entry` that takes a file path string and a message log string. Open the file in append mode and write the message onto a new line.",
        explanation: "Opening a file with mode `'a'` tells Python to write new data directly to the end of the file, keeping all existing content safe.",
        hint: "Open the file using `open(filepath, 'a')` and write the content followed by a newline: `file.write(message + '\\n')`.",
        starterCode: `def append_log_entry(filepath, message):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'log.txt', 'Err'", call: "str(type(append_log_entry('log.txt', 'Err')))", expected: "<class 'NoneType'>" },
        ],
      },
      {
        id: 82,
        title: "Key Error Safe Dictionary Getter Lookup",
        slug: "python-key-error-safe-dictionary-getter-lookup",
        seoTitle: "Python Prevent Key Errors | Error Handling | Repractiq",
        metaDescription: "Safely read keys from a dictionary using error handling fallbacks to catch KeyErrors.",
        tags: ["error handling", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `safe_dict_lookup` that takes a dictionary, a target key, and a default fallback value. Try to read the key, and if a `KeyError` occurs, catch it and return the default value.",
        explanation: "While you can use the `.get()` method to safely read keys, wrapping your dictionary lookups in a `try-except KeyError` block provides a clear, structured way to catch missing keys.",
        hint: "Wrap `return d[key]` in a try block, and handle missing keys using an `except KeyError:` clause.",
        starterCode: `def safe_dict_lookup(d, key, default):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{'a': 1}, 'a', 0", call: "safe_dict_lookup({'a': 1}, 'a', 0)", expected: "1" },
          { input: "{'a': 1}, 'b', 0", call: "safe_dict_lookup({'a': 1}, 'b', 0)", expected: "0" },
        ],
      },
      {
        id: 83,
        title: "OOP Car Class String Representation Override",
        slug: "python-oop-car-class-string-representation-override",
        seoTitle: "Python Override Class Str Method | OOP Basics | Repractiq",
        metaDescription: "Learn how to change the default print output of custom objects by overriding the built-in __str__ method.",
        tags: ["OOP basics", "classes"],
        difficulty: "Intermediate",
        type: "function",
        description: "Create a class named `Car` that initializes with `make` and `model` attributes. Override the built-in `__str__` method so that printing the object returns a custom string formatted as 'Make Model' (e.g., 'Toyota Corolla').",
        explanation: "The magic method `__str__(self)` defines what gets returned when you pass an object instance into the built-in `print()` or `str()` functions.",
        hint: "Declare a `def __str__(self):` method inside your class that returns an f-string: `return f'{self.make} {self.model}'`.",
        starterCode: `class Car:\n    # your class structure here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'Ford', 'Mustang'", call: "str(Car('Ford', 'Mustang'))", expected: "Ford Mustang" },
        ],
      },
      {
        id: 84,
        title: "Lambda Sorting List of Strings Alphabetically",
        slug: "python-lambda-sorting-list-of-strings-alphabetically",
        seoTitle: "Python Sort Strings by Last Character | Lambda Functions | Repractiq",
        metaDescription: "Sort a list of strings by their very last character using a custom sorting lambda function.",
        tags: ["lambda functions", "sorting", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_by_last_char` that takes a list of strings and uses an inline lambda function as a key to sort the words alphabetically by their last character.",
        explanation: "Passing the lambda function `key=lambda s: s[-1]` into `sorted()` tells Python to evaluate the items using their final character instead of their starting character.",
        hint: "Use `sorted(strings, key=lambda s: s[-1])` to sort the list by the last character of each string.",
        starterCode: `def sort_by_last_char(strings):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['apple', 'dog']", call: "str(sort_by_last_char(['apple', 'dog']))", expected: "['apple', 'dog']" },
          { input: "['cat', 'car']",   call: "str(sort_by_last_char(['cat', 'car']))",   expected: "['car', 'cat']" },
        ],
      },
      {
        id: 85,
        title: "Map Double Values Converter",
        slug: "python-map-double-values-converter",
        seoTitle: "Python Map Multiply List Values | Map/Filter | Repractiq",
        metaDescription: "Multiply all numbers in a list by two using the built-in functional map utility.",
        tags: ["map/filter", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `double_list_elements` that accepts a list of integers and uses the built-in functional `map()` method to return a new list where every number is multiplied by 2.",
        explanation: "The `map()` function efficiently transforms data collections. Passing an inline expression like `lambda x: x * 2` multiplies every element in the list by two.",
        hint: "Return your results wrapped in a standard list container: `list(map(lambda x: x * 2, numbers))`.",
        starterCode: `def double_list_elements(numbers):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 4]", call: "str(double_list_elements([1, 4]))", expected: "[2, 8]" },
          { input: "[]",     call: "str(double_list_elements([]))",     expected: "[]" },
        ],
      },
      {
        id: 86,
        title: "Nested Dictionaries Dynamic Key Updates",
        slug: "python-nested-dictionaries-dynamic-key-updates",
        seoTitle: "Python Update Nested Dictionary Values | Nested Data Structures | Repractiq",
        metaDescription: "Learn how to safely look up and update values inside deeply nested tracking dictionaries.",
        tags: ["nested data structures", "dictionaries"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `update_nested_stock` that updates an item's stock level inside a nested store inventory dictionary structure. If the store or item doesn't exist, create it.",
        explanation: "To update nested dictionaries safely, check if the outer key exists first. If it doesn't, initialize it as an empty dictionary before adding or updating the inner key-value pairs.",
        hint: "Use a check like `if store not in inventory: inventory[store] = {}`, then set the stock value: `inventory[store][item] = quantity`.",
        starterCode: `def update_nested_stock(inventory, store, item, quantity):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "{}, 's1', 'ink', 10", call: "str(update_nested_stock({}, 's1', 'ink', 10))", expected: "{'s1': {'ink': 10}}" },
        ],
      },
      {
        id: 87,
        title: "String Anagram Verification Checker",
        slug: "python-string-anagram-verification-checker",
        seoTitle: "Python Check Anagram Strings | String Manipulation | Repractiq",
        metaDescription: "Verify if two words are anagrams by comparing their sorted character counts.",
        tags: ["string manipulation", "sorting"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `is_anagram` that checks if two strings are anagrams of each other (meaning they contain the exact same characters in a different order), ignoring spaces and character case.",
        explanation: "You can easily verify anagrams by cleaning up the text, sorting the characters with the built-in `sorted()` function, and checking if the two sorted results match perfectly.",
        hint: "Clean both strings by removing spaces and converting them to lowercase, then check if `sorted(s1) == sorted(s2)`.",
        starterCode: `def is_anagram(str1, str2):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'Listen', 'Silent'", call: "is_anagram('Listen', 'Silent')", expected: "True" },
          { input: "'hello', 'world'",   call: "is_anagram('hello', 'world')",   expected: "False" },
        ],
      },
      {
        id: 88,
        title: "Sort Strings By Absolute Word Count Length",
        slug: "python-sort-strings-by-absolute-word-count-length",
        seoTitle: "Python Sort Sentences by Word Count | Sorting | Repractiq",
        metaDescription: "Sort a list of sentences by the number of words they contain using custom sorting lambdas.",
        tags: ["sorting", "lambda functions", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_sentences_by_words` that takes a list of sentences and returns a new list sorted in ascending order by the total number of words in each sentence.",
        explanation: "You can calculate the number of words in a sentence by splitting it by whitespace using `.split()`. Passing a lambda like `key=lambda s: len(s.split())` into `sorted()` orders the list by word count.",
        hint: "Pass `key=lambda s: len(s.split())` as the sorting key inside your `sorted()` function call.",
        starterCode: `def sort_sentences_by_words(sentences):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['a b c', 'hi']", call: "str(sort_sentences_by_words(['a b c', 'hi']))", expected: "['hi', 'a b c']" },
        ],
      },
      {
        id: 89,
        title: "Recursive Fibonacci Sequence Calculator",
        slug: "python-recursive-fibonacci-sequence-calculator",
        seoTitle: "Python Recursive Fibonacci Number | Recursion Basics | Repractiq",
        metaDescription: "Find the nth number in the Fibonacci sequence using classic linear recursive branches.",
        tags: ["recursion basics", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `fibonacci_recursive` that takes a non-negative integer `n` and returns the nth Fibonacci number in the sequence ($F_0=0, F_1=1$).",
        explanation: "The Fibonacci sequence can be calculated recursively by adding the two previous numbers together: `fib(n-1) + fib(n-2)`. The process stops when it hits the base cases ($n=0$ or $n=1$).",
        hint: "Base cases: if `n == 0`, return 0; if `n == 1`, return 1. Otherwise, return `fibonacci_recursive(n - 1) + fibonacci_recursive(n - 2)`.",
        starterCode: `def fibonacci_recursive(n):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "6", call: "fibonacci_recursive(6)", expected: "8" },
          { input: "0", call: "fibonacci_recursive(0)", expected: "0" },
        ],
      },
      {
        id: 90,
        title: "Sys Module Argument List Extractor",
        slug: "python-sys-module-argument-list-extractor",
        seoTitle: "Python Sys Argv Parameter Reading | Modules | Repractiq",
        metaDescription: "Read command-line arguments passed into a script using functions from the built-in sys module.",
        tags: ["modules"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `get_command_arguments` that uses Python's built-in `sys` module to extract and return the list of command-line arguments passed into the running script.",
        explanation: "The built-in `sys` module handles core interpreter interactions. Reading the list value `sys.argv` extracts the command-line arguments passed to the script during runtime.",
        hint: "Import `sys` and return the value of `sys.argv` directly from the function.",
        starterCode: `import sys\n\ndef get_command_arguments():\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "", call: "str(type(get_command_arguments()))", expected: "<class 'list'>" },
        ],
      },
      {
        id: 91,
        title: "Count Specific Word in File",
        slug: "python-count-specific-word-in-file",
        seoTitle: "Python Count Word in Text File | File Handling Concepts | Repractiq",
        metaDescription: "Learn how to read a text file and count all occurrences of a specific target keyword string.",
        tags: ["file handling concepts", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `count_word_in_file` that takes a file path string and a target word string. Open the file using a context manager, read its full contents, and return the integer count of how many times the exact target word appears (case-sensitive).",
        explanation: "Reading the full file content using `.read()` allows you to apply string operations like `.count(word)` directly on the file string text stream.",
        hint: "Open the file in read mode with a context manager, call `.read()`, and then use the string `.count(target_word)` method.",
        starterCode: `def count_word_in_file(filepath, target_word):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'log.txt', 'ERROR'", call: "str(type(count_word_in_file('log.txt', 'ERROR')))", expected: "<class 'int'>" },
        ],
      },
      {
        id: 92,
        title: "TypeError Safe Addition Fallback",
        slug: "python-type-error-safe-addition-fallback",
        seoTitle: "Python Handle TypeError Safely | Error Handling | Repractiq",
        metaDescription: "Catch TypeError exceptions during value additions to prevent runtime crashes using error recovery.",
        tags: ["error handling"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `safe_add` that attempts to add two parameters `a` and `b`. If a `TypeError` occurs (e.g., adding a string to an integer), catch the exception and return `None`.",
        explanation: "A `try-except TypeError` block lets your code attempt arithmetic operations dynamically without crashing when incompatible types are passed together.",
        hint: "Wrap `return a + b` inside a try block, and use an `except TypeError:` clause to catch type mismatches and return `None`.",
        starterCode: `def safe_add(a, b):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "5, 10",     call: "safe_add(5, 10)",             expected: "15" },
          { input: "5, 'ten'",  call: "str(safe_add(5, 'ten'))",     expected: "None" },
        ],
      },
      {
        id: 93,
        title: "OOP Rectangle Class Perimeter Method",
        slug: "python-oop-rectangle-class-perimeter",
        seoTitle: "Python Rectangle Class Perimeter | OOP Basics | Repractiq",
        metaDescription: "Design a class that models rectangular shapes and provides instance methods to evaluate geometrical metrics.",
        tags: ["OOP basics", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Create a class named `Rectangle` that initializes with `width` and `height` attributes. Add an instance method called `get_perimeter` that calculates and returns the perimeter of the rectangle ($2 \times (\text{width} + \text{height})$).",
        explanation: "Instance methods use the `self` keyword to access class property parameters dynamically, allowing you to build calculated attributes from initial values.",
        hint: "Inside `get_perimeter(self)`, compute and return `2 * (self.width + self.height)`.",
        starterCode: `class Rectangle:\n    # your class structure here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "4, 5", call: "Rectangle(4, 5).get_perimeter()", expected: "18" },
          { input: "10, 2", call: "Rectangle(10, 2).get_perimeter()", expected: "24" },
        ],
      },
      {
        id: 94,
        title: "Lambda Substring Checking Predicate",
        slug: "python-lambda-substring-checking-predicate",
        seoTitle: "Python Lambda Substring Containment | Lambda Functions | Repractiq",
        metaDescription: "Compose anonymous lambda predicate checks to evaluate substring existence in strings.",
        tags: ["lambda functions", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `get_contains_lambda` that returns an anonymous `lambda` function. The lambda should accept two arguments: a main `text` string and a `sub` string, and return `True` if `sub` exists inside `text`, otherwise `False`.",
        explanation: "The `in` operator evaluates containment in Python. Combining it with a lambda expression creates a lightweight, portable predicate function.",
        hint: "Return an anonymous lambda defined as `lambda text, sub: sub in text`.",
        starterCode: `def get_contains_lambda():\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "", call: "f = get_contains_lambda(); f('apple', 'pp')", expected: "True" },
          { input: "", call: "f = get_contains_lambda(); f('apple', 'orange')", expected: "False" },
        ],
      },
      {
        id: 95,
        title: "Filter Palindrome Strings from List",
        slug: "python-filter-palindrome-strings-list",
        seoTitle: "Python Filter Palindromes | Map/Filter | Repractiq",
        metaDescription: "Extract symmetrical palindrome words from a collection using functional filter components.",
        tags: ["map/filter", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `extract_palindromes` that takes a list of strings and uses the built-in functional `filter()` method to keep only the strings that are palindromes (words that read the same backwards as forwards).",
        explanation: "The functional `filter()` interface processes elements against conditional operations. Passing the matching test `lambda s: s == s[::-1]` separates valid palindromes.",
        hint: "Combine a filter operation with string slicing: `list(filter(lambda s: s == s[::-1], string_list))`.",
        starterCode: `def extract_palindromes(string_list):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['racecar', 'cool']", call: "str(extract_palindromes(['racecar', 'cool']))", expected: "['racecar']" },
          { input: "['abc', 'xyz']",      call: "str(extract_palindromes(['abc', 'xyz']))",      expected: "[]" },
        ],
      },
      {
        id: 96,
        title: "Nested Matrix Row Sum Tracker",
        slug: "python-nested-matrix-row-sum-tracker",
        seoTitle: "Python Sum Matrix Rows | Nested Data Structures | Repractiq",
        metaDescription: "Calculate individual row sums out of sub-array grid arrays to track matrix dimension totals.",
        tags: ["nested data structures", "lists", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sum_matrix_rows` that takes a 2D list matrix of numbers and returns a new 1D list containing the sum total of each individual row matrix.",
        explanation: "Iterating through multi-tiered lists exposes each sub-list array row sequentially. Applying the standard `sum()` function converts each inner array into a single summary total.",
        hint: "Use a simple list comprehension to loop through each row: `[sum(row) for row in matrix]`.",
        starterCode: `def sum_matrix_rows(matrix):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[[1, 2], [3, 4]]", call: "str(sum_matrix_rows([[1, 2], [3, 4]]))", expected: "[3, 7]" },
          { input: "[[], [5]]",        call: "str(sum_matrix_rows([[], [5]]))",        expected: "[0, 5]" },
        ],
      },
      {
        id: 97,
        title: "String Digit Character Isolator Extraction",
        slug: "python-string-digit-character-isolator-extraction",
        seoTitle: "Python Extract Digits from String | String Manipulation | Repractiq",
        metaDescription: "Extract numeric numerical text characters out of mixed alphanumeric string formats.",
        tags: ["string manipulation"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `extract_digits` that takes a string and returns a new string containing only the numerical digit characters (`0-9`) found in the original text.",
        explanation: "The `.isdigit()` string attribute method determines if a specific character is a number, making it easy to filter out letters, punctuation, and spaces.",
        hint: "Loop through the characters, filter with `c.isdigit()`, and recombine the matching numbers using `''.join(...)`.",
        starterCode: `def extract_digits(text):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "'code2026'", call: "extract_digits('code2026')", expected: "2026" },
          { input: "'abc'",      call: "extract_digits('abc')",      expected: "" },
        ],
      },
      {
        id: 98,
        title: "Sort Strings Alphabetically by Length and Value",
        slug: "python-sort-strings-alphabetically-by-length-value",
        seoTitle: "Python Sort Strings Multi Key | Sorting | Repractiq",
        metaDescription: "Sort collections of text using compound sorting configurations to organize strings by length and then alphabetically.",
        tags: ["sorting", "strings"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `sort_by_length_then_alphabet` that takes a list of strings and returns a new list sorted in ascending order primary by length, and secondary alphabetically for strings of the same length.",
        explanation: "Python sorting algorithms are stable and support multi-key sorting tuples. Returning a custom comparison tuple like `(len(s), s)` orders elements precisely across multiple sorting criteria.",
        hint: "Pass a tuple configuration directly as your sorting key rule: `sorted(strings, key=lambda s: (len(s), s))`.",
        starterCode: `def sort_by_length_then_alphabet(strings):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "['ccc', 'a', 'b']", call: "str(sort_by_length_then_alphabet(['ccc', 'a', 'b']))", expected: "['a', 'b', 'ccc']" },
        ],
      },
      {
        id: 99,
        title: "Recursive Count Occurrences in List",
        slug: "python-recursive-count-occurrences-in-list",
        seoTitle: "Python Recursive Count List Elements | Recursion Basics | Repractiq",
        metaDescription: "Count specific list values recursively without applying iterative array loops or container methods.",
        tags: ["recursion basics", "lists"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `count_recursive` that takes a list of values and a target item. Recursively evaluate the collection to return the total integer count of the target item without loops or `.count()`.",
        explanation: "You can track item counts recursively by checking if the first element matches the target value, adding 1 if it does, and then making a recursive call with the remaining slice of the list.",
        hint: "Base case: if the list is empty, return 0. Otherwise, evaluate the first element: `(1 if items[0] == target else 0) + count_recursive(items[1:], target)`.",
        starterCode: `def count_recursive(items, target):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "[1, 2, 1, 3], 1", call: "count_recursive([1, 2, 1, 3], 1)", expected: "2" },
          { input: "[], 5",           call: "count_recursive([], 5)",           expected: "0" },
        ],
      },
      {
        id: 100,
        title: "Math Module Greatest Common Divisor",
        slug: "python-math-module-greatest-common-divisor",
        seoTitle: "Python Math GCD Calculation | Modules | Repractiq",
        metaDescription: "Calculate the greatest common divisor between integers using utilities from Python's math module.",
        tags: ["modules", "math"],
        difficulty: "Intermediate",
        type: "function",
        description: "Write a function called `calculate_gcd` that takes two integers `a` and `b`, imports Python's built-in `math` module, and returns their Greatest Common Divisor (GCD).",
        explanation: "The core `math` package provides a highly efficient, built-in `math.gcd(a, b)` function to find the largest positive integer that divides both numbers without a remainder.",
        hint: "Import the `math` module and pass your inputs directly into `math.gcd(a, b)`.",
        starterCode: `import math\n\ndef calculate_gcd(a, b):\n    # your code here\n    pass\n`,
        expectedOutput: "",
        testCases: [
          { input: "54, 24", call: "calculate_gcd(54, 24)", expected: "6" },
          { input: "7, 3",   call: "calculate_gcd(7, 3)",   expected: "1" },
        ],
      },
  ];